import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CURRENCY_SYMBOLS } from '../../utils/currencyUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  box-shadow: 
    8px 8px 20px rgba(0, 0, 0, 0.1),
    -8px -8px 20px rgba(255, 255, 255, 0.8);
  transform: ${props => props.$isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #8c96a5;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #4a6fa1;
  }
`;

const InputGroup = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.7);
  margin-bottom: 20px;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: #f0f3f9;
  color: #333;
  font-size: 16px;
  
  &:focus {
    outline: none;
    background: #e6ebf5;
  }
`;

const CurrencySelectWrapper = styled.div`
  position: relative;
  width: 60px;
  background: #e6ebf5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CurrencySelect = styled.select`
  width: 100%;
  height: 100%;
  padding: 0 16px 0 10px;
  border: none;
  background: transparent;
  color: #4a6fa1;
  font-weight: 500;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #4a6fa1;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #4a6fa1;
  margin-bottom: 10px;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(145deg, #4a6fa1, #5d82b3);
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1),
              -2px -2px 5px rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #4263a1, #5275b3);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1),
                -1px -1px 3px rgba(255, 255, 255, 0.1);
  }
`;

const BudgetModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialLimit = 0, 
  initialCurrency = 'UAH',
  category 
}) => {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(initialLimit);
  const [currency, setCurrency] = useState(initialCurrency);

  // Обновляем состояния при изменении пропсов
  useEffect(() => {
    setLimit(initialLimit);
    setCurrency(initialCurrency);
  }, [initialLimit, initialCurrency, isOpen]);

  const handleSubmit = () => {
    if (!limit || isNaN(parseFloat(limit)) || parseFloat(limit) <= 0) {
      alert(t('enter_valid'));
      return;
    }
    
    onSubmit(parseFloat(limit), currency);
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent 
        $isOpen={isOpen} 
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>{t('modal_title')}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <CategoryName>{category}</CategoryName>
        
        <InputGroup>
          <Input
            type="number"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder={t('modal_enter_new')}
            min="0"
            step="0.01"
          />
<CurrencySelectWrapper>
  <CurrencySelect
    value={currency}
    onChange={(e) => setCurrency(e.target.value)}
  >
    {Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => (
      <option key={code} value={code}>
        {symbol}
      </option>
    ))}
  </CurrencySelect>
  <SelectArrow />
</CurrencySelectWrapper>
        </InputGroup>
        
        <SaveButton onClick={handleSubmit}>
          {t('modal_save')}
        </SaveButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default BudgetModal;